import { type UseQueryOptions, useQuery } from '@tanstack/react-query';

// Moved to its own type in order to improve error messages
type AnyFunction = (...args: any[]) => any;

export type AllowedUseQueryOptions<TResponse, TError = Error, TData = TResponse> = Omit<
  UseQueryOptions<TResponse, TError, TData>,
  'queryKey' | 'queryFn'
>;

export interface UseApiProps<
  TQuery,
  TResponse,
  TError = Error,
  TData = TResponse,
> extends AllowedUseQueryOptions<TResponse, TError, TData> {
  api: {
    /**
     * This must be globally unique across the project/app.
     * The only thing that we can guarantee to be 1:1 with the API is the endpoint itself.
     * The `endpoint` is used to generate the `uniqueKey` for the query, in combination with `queryParams`.
     */
    endpointId: string;
    fetchData: (queryParams?: TQuery) => Promise<TResponse>;
  };
  queryParams?: TQuery;
}

/**
 * Wraps `useQuery` to manage API response states for an array of data from an API endpoint
 * with optional query parameters.
 * Example API:
 * `GET /users` to get all users
 * `GET /users?firstName=karam` to get all users with firstName of `karam`
 */
export function useApi<TQuery, TResponse, TError = Error, TData = TResponse>(
  props: UseApiProps<TQuery, TResponse, TError, TData>
) {
  const { api, queryParams, ...queryOptions } = props;
  const { endpointId } = api;
  return useQuery({
    ...queryOptions,
    queryKey: [endpointId, queryParams],
    queryFn: async () => {
      const payload = await api.fetchData(queryParams);
      return payload;
    },
  });
}

export interface UseApiByIdProps<
  TApiFunctionName extends string,
  TResponse,
  TError = Error,
  TData = TResponse,
> extends AllowedUseQueryOptions<TResponse, TError, TData> {
  apiFunctionName: TApiFunctionName;
  api: {
    baseEndpoint: string;
  } & Record<TApiFunctionName, (id: string) => Promise<TResponse>>;
  id: string;
}

/**
 * Wraps `useQuery` to manage API response states for data from an API endpoint
 * with a required id.
 * Example API:
 * `GET /users/:id` to get one user by id
 */
export function useApiById<
  TApiFunctionName extends string,
  TResponse,
  TError = Error,
  TData = TResponse,
>(props: UseApiByIdProps<TApiFunctionName, TResponse, TError, TData>) {
  const { api, apiFunctionName, id, ...queryOptions } = props;
  const { baseEndpoint } = api;
  return useQuery({
    ...queryOptions,
    queryKey: [baseEndpoint, apiFunctionName, id],
    queryFn: async () => {
      const payload = await api[apiFunctionName](id);
      return payload;
    },
  });
}

/**
 * Adapt a vivaa-api client method to be used with TanStack Query.
 */
export function getQueryProps<
  TFunctionName extends keyof TClient & string,
  TClient extends Record<TFunctionName, AnyFunction> & {
    baseEndpoint: string;
  },
>(
  // Take in client and function name separately to avoid forcing callers
  // to bind the function to the client themselves.
  client: TClient,
  functionName: TFunctionName,
  ...args: Parameters<TClient[TFunctionName]>
): {
  queryKey: unknown[];
  queryFn: () => ReturnType<TClient[TFunctionName]>;
} {
  return {
    queryKey: [`${client.baseEndpoint}:${functionName}`, ...args],
    // The return type is set correctly on our type signature, but inside this
    // function it's inferred as `any`.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    queryFn: () => client[functionName](...args),
  };
}
